import type L from 'leaflet';

export interface MapBounds {
  readonly south: number;
  readonly west: number;
  readonly north: number;
  readonly east: number;
}

export interface MapViewport extends MapBounds {
  readonly zoom: number;
}

export interface MapCoordinate {
  readonly lat: number;
  readonly lng: number;
}

export interface MapTile {
  readonly zoom: number;
  readonly tileX: number;
  readonly tileY: number;
}

export interface MapSelection extends MapBounds {
  readonly polygon?: readonly MapCoordinate[];
  readonly clusterTiles?: readonly MapTile[];
}

export function mapBounds(bounds: L.LatLngBounds): MapBounds {
  return {
    south: bounds.getSouth(),
    west: bounds.getWest(),
    north: bounds.getNorth(),
    east: bounds.getEast(),
  };
}

export function mapViewport(
  bounds: L.LatLngBounds,
  zoom: number,
): MapViewport {
  return { ...mapBounds(bounds), zoom };
}

export function sameMapViewport(
  left: MapViewport | undefined,
  right: MapViewport,
): boolean {
  return left?.south === right.south
    && left.west === right.west
    && left.north === right.north
    && left.east === right.east
    && left.zoom === right.zoom;
}

export function mapSelection(
  bounds: L.LatLngBounds,
  polygon?: readonly L.LatLng[],
  clusterTiles?: readonly MapTile[],
): MapSelection {
  return {
    ...mapBounds(bounds),
    polygon: polygon?.map(point => ({ lat: point.lat, lng: point.lng })),
    clusterTiles,
  };
}

/** Geographic coordinates shared by location previews and main-map actions. */
export function hasMapCoordinates(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): boolean {
  return latitude != null && longitude != null
    && Number.isFinite(latitude) && Number.isFinite(longitude)
    && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180;
}
